import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FiClipboard, FiFile, FiFileText, FiUpload } from "react-icons/fi";
import apiClient, { withApiPath } from "../../utils/apiClient";
import styles from "./DocumentsConsole.module.css";

const TYPES = [
  ["CARRIER_RATE_CONFIRMATION", "Rate Confirmation"],
  ["BILL_OF_LADING", "Bill of Lading"],
  ["POD", "Proof of Delivery"],
  ["OTHER", "Other"],
];
const uuid = () =>
  window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
const splitEmails = (value) =>
  value
    .split(/[;,\s]+/)
    .map((v) => v.trim())
    .filter(Boolean);
const when = (value) =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";
const typeLabel = (type) =>
  TYPES.find(([value]) => value === type)?.[1] || "Document";

const EMPTY_STATE_CONTENT = {
  CARRIER_RATE_CONFIRMATION: {
    title: "Rate Confirmation",
    status: "Not generated",
    description:
      "Generate a carrier Rate Confirmation from the current shipment data.",
    actionLabel: "Generate Rate Confirmation",
    icon: FiFileText,
  },
  BILL_OF_LADING: {
    title: "Bill of Lading",
    status: "Not generated",
    description: "Generate a Bill of Lading from the current shipment data.",
    actionLabel: "Generate BOL",
    icon: FiClipboard,
  },
  POD: {
    title: "Proof of Delivery",
    status: "Not uploaded",
    description: "Upload the signed Proof of Delivery for this shipment.",
    actionLabel: "Upload PDF",
    icon: FiUpload,
  },
  OTHER: {
    title: "Other Documents",
    status: "Not uploaded",
    description: "Upload another shipment document.",
    actionLabel: "Upload Document",
    icon: FiFile,
  },
};

const DOCUMENT_ERROR_MESSAGES = {
  missing_issuer_profile: "Issuer profile must be configured.",
  missing_carrier_rate: "Carrier compensation is required.",
  missing_rate_confirmation_terms:
    "Approved Rate Confirmation Terms are required.",
  missing_rate_confirmation_billing_requirements:
    "Approved Rate Confirmation Billing Requirements are required.",
  missing_bol_number: "BOL number is required.",
  stale_revision: "Shipment changed. Sync/reload before generating.",
  render_failed: "PDF rendering failed in the document service.",
  storage_failed: "Document file could not be stored.",
};

export const documentOperationError = (error) => {
  const status = error?.response?.status;
  const body = error?.response?.data;
  if (process.env.NODE_ENV === "development")
    console.error("Document request failed", { status, body });
  const code =
    body?.code === "prerequisites_not_met"
      ? body?.errors?.[0]?.code
      : body?.code || body?.errors?.[0]?.code;
  if (code === "stale_revision" || status === 409)
    return DOCUMENT_ERROR_MESSAGES.stale_revision;
  return (
    DOCUMENT_ERROR_MESSAGES[code] ||
    body?.message ||
    body?.detail ||
    body?.failure_message ||
    "Document generation failed. See server logs or retry."
  );
};

export const DocumentList = ({ series, selectedDocumentType, onSelect }) => (
  <aside className={styles.list} data-testid="document-list">
    <h3>DOCUMENTS</h3>
    {series.map((item) => (
      <button
        key={item.type}
        className={selectedDocumentType === item.type ? styles.active : ""}
        onClick={() => onSelect(item.type, item.document)}
      >
        <strong>{item.label.toUpperCase()}</strong>
        <span>
          {item.document
            ? `v${item.document.version} · ${item.document.is_current ? "CURRENT" : item.document.status}`
            : item.type === "OTHER"
              ? "NO DOCUMENTS"
              : "NOT GENERATED"}
        </span>
        <small data-status={item.document?.status}>
          {item.document?.status ||
            (item.type === "POD" ? "NOT UPLOADED" : "NOT GENERATED")}
        </small>
      </button>
    ))}
  </aside>
);

export const DocumentEmptyState = ({
  title,
  status,
  description,
  icon: Icon,
  actionLabel,
  onAction,
  actionDisabled = false,
}) => (
  <div className={styles.emptyState} data-testid="document-empty-state">
    <div className={styles.emptyIcon} aria-hidden="true">
      <Icon />
    </div>
    <h2>{title}</h2>
    <span className={styles.emptyStatus}>
      <i aria-hidden="true" />
      {status}
    </span>
    <p>{description}</p>
    {onAction && (
      <button
        className={styles.emptyAction}
        disabled={actionDisabled}
        onClick={onAction}
      >
        {actionLabel}
      </button>
    )}
  </div>
);

export const DocumentPreview = React.forwardRef(
  (
    {
      url,
      document,
      documentType,
      loading,
      error,
      onRetry,
      onEmptyAction,
      canAct,
    },
    ref,
  ) => {
    const emptyContent = EMPTY_STATE_CONTENT[documentType];
    return (
      <main className={styles.preview} data-testid="document-preview">
        {loading ? (
          <p>Loading exact version…</p>
        ) : error ? (
          <div className={styles.emptyState}>
            <strong>PREVIEW UNAVAILABLE</strong>
            <p>{error}</p>
            <button onClick={onRetry}>Retry Preview</button>
          </div>
        ) : url && document ? (
          <iframe
            ref={ref}
            title={`${document.name} version ${document.version}`}
            src={url}
          />
        ) : (
          emptyContent && (
            <DocumentEmptyState
              {...emptyContent}
              onAction={canAct ? onEmptyAction : undefined}
            />
          )
        )}
      </main>
    );
  },
);

export const DocumentVersionHistory = ({ versions, selected, onSelect }) => (
  <section>
    <h4>VERSION HISTORY</h4>
    {versions.map((version) => (
      <button
        className={`${styles.history} ${selected?.public_id === version.public_id ? styles.selectedHistory : ""}`}
        key={version.public_id}
        onClick={() => onSelect(version)}
      >
        <strong>
          v{version.version} · {version.is_current ? "CURRENT" : version.status}
        </strong>
        <span>{when(version.generated_at || version.created_at)}</span>
      </button>
    ))}
  </section>
);

export const DocumentEmailHistory = ({ rows, onRetry }) => (
  <section>
    <h4>EMAIL HISTORY</h4>
    {!rows.length && <p className={styles.muted}>No delivery attempts.</p>}
    {rows.map((row) => (
      <div className={styles.delivery} key={row.public_id}>
        <strong>{row.status}</strong>
        <span>{when(row.sent_at || row.requested_at)}</span>
        <span>{row.to_recipients.join(", ")}</span>
        {row.status === "FAILED" && (
          <button onClick={() => onRetry(row.public_id)}>Retry</button>
        )}
      </div>
    ))}
  </section>
);

export const DocumentDetails = ({
  selected,
  documentType,
  versions,
  deliveries,
  onSelect,
  onRetry,
}) => (
  <aside className={styles.details} data-testid="document-details">
    <h3>DETAILS</h3>
    {selected ? (
      <>
        <h2>{selected.name}</h2>
        <p>
          Version {selected.version} · {selected.status}
        </p>
        <dl>
          <dt>
            {selected.source_type === "UPLOADED" ? "Uploaded" : "Generated"}
          </dt>
          <dd>{when(selected.generated_at || selected.created_at)}</dd>
          <dt>By</dt>
          <dd>
            {selected.generated_by?.name || selected.uploaded_by?.name || "—"}
          </dd>
          <dt>Source rev</dt>
          <dd>{selected.source_load_revision ?? "—"}</dd>
          <dt>File size</dt>
          <dd>
            {selected.file_size
              ? `${Math.ceil(selected.file_size / 1024)} KB`
              : "—"}
          </dd>
        </dl>
        {selected.changed_since_generation && (
          <p className={styles.stale}>SHIPMENT CHANGED SINCE GENERATED</p>
        )}
        <DocumentVersionHistory
          versions={versions}
          selected={selected}
          onSelect={onSelect}
        />
        <DocumentEmailHistory rows={deliveries} onRetry={onRetry} />
      </>
    ) : (
      <p className={styles.muted}>
        {typeLabel(documentType)} has no document version yet.
      </p>
    )}
  </aside>
);

const ModalSurface = ({ children, label, onClose, wide = false }) => {
  const dialogRef = useRef(null);
  const returnFocusRef = useRef(document.activeElement);
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    dialogRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeRef.current();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      returnFocusRef.current?.focus?.();
    };
  }, []);
  return (
    <div className={styles.overlay} data-testid="document-modal-backdrop">
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={`${styles.dialog} ${wide ? styles.settings : ""}`}
      >
        {children}
      </div>
    </div>
  );
};

const GenerateDocumentModal = ({
  type,
  settings,
  updating,
  operationError,
  onClose,
  onSubmit,
}) => {
  const [notes, setNotes] = useState(false);
  const errors = settings?.prerequisites?.[type] || [];
  return (
    <ModalSurface label="Generate document" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!errors.length) onSubmit({ include_route_notes: notes });
        }}
      >
        <h2>{updating ? "UPDATE" : "GENERATE"} DOCUMENT</h2>
        {operationError && (
          <p className={styles.errorBox} role="alert">
            {operationError}
          </p>
        )}
        {errors.length ? (
          <div className={styles.errorBox}>
            <strong>Cannot generate document</strong>
            {errors.map((item) => (
              <p key={item.code}>{item.message}</p>
            ))}
          </div>
        ) : (
          <label>
            <input
              type="checkbox"
              checked={notes}
              onChange={(e) => setNotes(e.target.checked)}
            />{" "}
            Include route notes
          </label>
        )}
        <footer>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button disabled={errors.length} type="submit">
            {updating ? "Update" : "Generate"}
          </button>
        </footer>
      </form>
    </ModalSurface>
  );
};

const UploadDocumentModal = ({
  initialType,
  operationError,
  onClose,
  onSubmit,
}) => {
  const [type, setType] = useState(initialType === "OTHER" ? "OTHER" : "POD");
  const [file, setFile] = useState();
  return (
    <ModalSurface label="Upload document" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(type, file);
        }}
      >
        <h2>UPLOAD DOCUMENT</h2>
        {operationError && (
          <p className={styles.errorBox} role="alert">
            {operationError}
          </p>
        )}
        <label>
          Type
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="POD">Proof of Delivery</option>
            <option value="OTHER">Other</option>
          </select>
        </label>
        <label>
          PDF
          <input
            required
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => setFile(e.target.files[0])}
          />
        </label>
        <footer>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button disabled={!file}>Upload</button>
        </footer>
      </form>
    </ModalSurface>
  );
};

export const EmailDocumentModal = ({
  selected,
  documents,
  operationError,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState({
    to: "",
    cc: "",
    bcc: "",
    reply: "",
    subject: `${selected.name} — Load document`,
    message: "",
    attachmentIds: [selected.public_id],
  });
  const set = (key, value) => setForm((old) => ({ ...old, [key]: value }));
  return (
    <ModalSurface label="Email document" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(form);
        }}
      >
        <h2>EMAIL DOCUMENT</h2>
        {operationError && (
          <p className={styles.errorBox} role="alert">
            {operationError}
          </p>
        )}
        {["to", "cc", "bcc", "reply"].map((key) => (
          <label key={key}>
            {key.toUpperCase()}
            <input
              required={key === "to"}
              value={form[key]}
              onChange={(e) => set(key, e.target.value)}
            />
          </label>
        ))}
        <label>
          SUBJECT
          <input
            required
            value={form.subject}
            onChange={(e) => set("subject", e.target.value)}
          />
        </label>
        <label>
          MESSAGE
          <textarea
            value={form.message}
            onChange={(e) => set("message", e.target.value)}
          />
        </label>
        <fieldset>
          <legend>ATTACHMENTS</legend>
          {documents
            .filter((d) => d.status === "AVAILABLE")
            .map((d) => (
              <label key={d.public_id}>
                <input
                  type="checkbox"
                  checked={form.attachmentIds.includes(d.public_id)}
                  onChange={() =>
                    set(
                      "attachmentIds",
                      form.attachmentIds.includes(d.public_id)
                        ? form.attachmentIds.filter((id) => id !== d.public_id)
                        : [...form.attachmentIds, d.public_id],
                    )
                  }
                />
                {d.name} v{d.version}
              </label>
            ))}
        </fieldset>
        <footer>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button disabled={!form.attachmentIds.length}>Send</button>
        </footer>
      </form>
    </ModalSurface>
  );
};

export const DocumentSettings = ({ loadId, onClose }) => {
  const empty = {
    legal_name: "",
    dba_name: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
    phone: "",
    email: "",
    country: "",
  };
  const [data, setData] = useState(null),
    [company, setCompany] = useState(empty),
    [editingCompany, setEditingCompany] = useState(false),
    [draft, setDraft] = useState({
      document_type: "CARRIER_RATE_CONFIRMATION",
      content_kind: "RATE_CONFIRMATION_TERMS",
      title: "",
      content: "",
    }),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true);
  const mounted = useRef(true);
  const requestId = useRef(0);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  const refresh = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError("");
    try {
      const response = await apiClient.get(
        withApiPath(`/document-configuration/?load_id=${loadId}`),
      );
      if (mounted.current && id === requestId.current) setData(response.data);
    } catch (_) {
      if (mounted.current && id === requestId.current)
        setError("Document settings could not be loaded.");
    } finally {
      if (mounted.current && id === requestId.current) setLoading(false);
    }
  }, [loadId]);
  useEffect(() => {
    refresh();
  }, [refresh]);
  const active = data?.issuer_profiles?.find((p) => p.active);
  useEffect(() => {
    if (data?.account) setCompany(data.account);
  }, [data]);
  const run = async (action) => {
    setError("");
    try {
      await action();
      await refresh();
    } catch (e) {
      if (mounted.current) setError(documentOperationError(e));
    }
  };
  const saveCompany = (e) => {
    e.preventDefault();
    run(async () => {
      await apiClient.put(withApiPath("/document-configuration/"), {
        ...company,
        load_id: loadId,
      });
      if (mounted.current) setEditingCompany(false);
    });
  };
  const saveDraft = (e) => {
    e.preventDefault();
    run(async () => {
      await apiClient.post(withApiPath("/document-configuration/content/"), {
        ...draft,
        issuer_id: active?.id,
        load_id: loadId,
      });
      if (mounted.current)
        setDraft((old) => ({ ...old, title: "", content: "" }));
    });
  };
  const transition = (id, action) =>
    run(() =>
      apiClient.put(withApiPath(`/document-configuration/content/${id}/`), {
        action,
        load_id: loadId,
      }),
    );
  return (
    <ModalSurface label="Document settings" onClose={onClose} wide>
      <header>
        <h2>DOCUMENT SETTINGS</h2>
        <button onClick={onClose}>Close</button>
      </header>
      {loading && <p>Loading settings…</p>}
      {error && (
        <p className={styles.errorBox} role="alert">
          {error} <button onClick={refresh}>Retry</button>
        </p>
      )}
      {!loading && data?.account && (
        <section className={styles.organization}>
          <h3>ISSUING ORGANIZATION</h3>
          <strong>{company.display_name || company.legal_name}</strong>
          {company.legal_name && company.legal_name !== company.display_name && (
            <span>Legal name: {company.legal_name}</span>
          )}
          {company.address && <span>{company.address}</span>}
          {(company.city || company.state || company.postal_code) && (
            <span>
              {[company.city, company.state].filter(Boolean).join(", ")}
              {company.postal_code ? ` ${company.postal_code}` : ""}
            </span>
          )}
          {company.country && <span>{company.country}</span>}
          {(company.phone || company.email) && (
            <span>
              {company.phone}
              {company.phone && company.email ? " · " : ""}
              {company.email && <a href={`mailto:${company.email}`}>{company.email}</a>}
            </span>
          )}
          {!!data.missing_required_fields?.length && (
            <div className={styles.incomplete} role="status">
              <strong>Company information incomplete</strong>
              <span>
                Complete: {data.missing_required_fields.join(", ")}.
              </span>
            </div>
          )}
          <footer>
            <small>Synced from Account</small>
            <button type="button" onClick={() => setEditingCompany(true)}>
              {data.missing_required_fields?.length
                ? "Complete company information"
                : "Edit company information"}
            </button>
          </footer>
        </section>
      )}
      {editingCompany && (
        <form className={styles.companyEditor} onSubmit={saveCompany}>
          <h3>EDIT COMPANY INFORMATION</h3>
          {Object.keys(empty).map((key) => (
            <label key={key}>
              {key.replaceAll("_", " ").toUpperCase()}
              <input
                type={key === "email" ? "email" : "text"}
                required={["legal_name", "address", "city", "state", "postal_code"].includes(key)}
                value={company[key] || ""}
                onChange={(event) => setCompany((old) => ({ ...old, [key]: event.target.value }))}
              />
            </label>
          ))}
          <footer>
            <button type="button" onClick={() => { setCompany(data.account); setEditingCompany(false); }}>Cancel</button>
            <button>Save</button>
          </footer>
        </form>
      )}
      <form onSubmit={saveDraft}>
        <h3>LEGAL CONTENT REVISION</h3>
        <select
          value={draft.content_kind}
          onChange={(e) =>
            setDraft((old) => ({
              ...old,
              content_kind: e.target.value,
              document_type: e.target.value.startsWith("BOL_")
                ? "BILL_OF_LADING"
                : "CARRIER_RATE_CONFIRMATION",
            }))
          }
        >
          {[
            "RATE_CONFIRMATION_TERMS",
            "RATE_CONFIRMATION_BILLING_REQUIREMENTS",
            "BOL_SHIPPER_CERTIFICATION",
            "BOL_CARRIER_CERTIFICATION",
            "BOL_CONSIGNEE_CERTIFICATION",
          ].map((k) => (
            <option key={k}>{k}</option>
          ))}
        </select>
        <input
          placeholder="Revision title"
          required
          value={draft.title}
          onChange={(e) =>
            setDraft((old) => ({ ...old, title: e.target.value }))
          }
        />
        <textarea
          placeholder="Approved plain text"
          required
          value={draft.content}
          onChange={(e) =>
            setDraft((old) => ({ ...old, content: e.target.value }))
          }
        />
        <button disabled={!active}>Save draft</button>
      </form>
      <h3>REVISION HISTORY</h3>
      {data?.content_revisions?.map((r) => (
        <div className={styles.revision} key={r.id}>
          <strong>
            {r.content_kind} v{r.version}
          </strong>
          <span>{r.status}</span>
          {r.status === "DRAFT" && (
            <button onClick={() => transition(r.id, "activate")}>
              Activate
            </button>
          )}
          {r.status === "ACTIVE" && (
            <button onClick={() => transition(r.id, "retire")}>Retire</button>
          )}
          <p>{r.title}</p>
        </div>
      ))}
    </ModalSurface>
  );
};

export default function DocumentsConsole({ loadId, onClose }) {
  const [documents, setDocuments] = useState([]),
    [settings, setSettings] = useState(null);
  const [selection, setSelection] = useState({
    type: null,
    seriesId: null,
    versionId: null,
  });
  const [versions, setVersions] = useState([]),
    [deliveries, setDeliveries] = useState([]);
  const [preview, setPreview] = useState({ url: null, error: "" }),
    [loading, setLoading] = useState({
      documents: true,
      preview: false,
      settings: false,
    });
  const [operations, setOperations] = useState({
      generation: 0,
      upload: 0,
      email: 0,
      archive: 0,
    }),
    [error, setError] = useState(""),
    [modal, setModal] = useState(null),
    [previewAttempt, setPreviewAttempt] = useState(0);
  const frame = useRef(),
    mounted = useRef(true),
    refreshId = useRef(0),
    selectionId = useRef(0),
    previewUrl = useRef(null),
    selectionRef = useRef(selection);
  useEffect(() => {
    selectionRef.current = selection;
  }, [selection]);
  const replacePreview = useCallback((url) => {
    if (previewUrl.current && previewUrl.current !== url)
      URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = url;
    setPreview({ url, error: "" });
  }, []);
  useEffect(
    () => () => {
      mounted.current = false;
      refreshId.current += 1;
      selectionId.current += 1;
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
      previewUrl.current = null;
    },
    [],
  );
  const reconcile = useCallback(
    (normalized, preferred = selectionRef.current, config = null) => {
      const allowed = TYPES.filter(
        ([type]) =>
          type !== "CARRIER_RATE_CONFIRMATION" ||
          config?.capabilities?.can_view_carrier_financials,
      ).map(([type]) => type);
      const type = allowed.includes(preferred?.type)
        ? preferred.type
        : allowed[0] || null;
      const sameType = normalized.filter((d) => d.document_type === type);
      const exact = sameType.find((d) => d.public_id === preferred?.versionId);
      const available = sameType.filter((d) => d.status !== "ARCHIVED");
      const sameSeries =
        available.find(
          (d) => d.series_id === preferred?.seriesId && d.is_current,
        ) || available.find((d) => d.series_id === preferred?.seriesId);
      const current =
        available.find((d) => d.is_current) ||
        available.sort((a, b) => b.version - a.version)[0];
      const chosen = exact || sameSeries || current || null;
      return {
        type,
        seriesId: chosen?.series_id || null,
        versionId: chosen?.public_id || null,
      };
    },
    [],
  );
  const refresh = useCallback(
    async (preferred) => {
      const id = ++refreshId.current;
      setLoading((old) => ({ ...old, documents: true }));
      setError("");
      try {
        const [docs, config] = await Promise.all([
          apiClient.get(withApiPath(`/loads/${loadId}/documents/`)),
          apiClient.get(withApiPath(`/loads/${loadId}/document-settings/`)),
        ]);
        if (!mounted.current || id !== refreshId.current) return false;
        const normalized = docs.data.filter(
          (d) => d.document_type !== "TEST_DOCUMENT",
        );
        setDocuments(normalized);
        setSettings(config.data);
        setSelection(
          reconcile(normalized, preferred || selectionRef.current, config.data),
        );
        return true;
      } catch (_) {
        if (mounted.current && id === refreshId.current)
          setError("Documents could not be loaded.");
        return false;
      } finally {
        if (mounted.current && id === refreshId.current)
          setLoading((old) => ({ ...old, documents: false }));
      }
    },
    [loadId, reconcile],
  );
  useEffect(() => {
    refresh();
  }, [refresh]);
  const selected = useMemo(
    () => documents.find((d) => d.public_id === selection.versionId) || null,
    [documents, selection.versionId],
  );
  useEffect(() => {
    const id = ++selectionId.current;
    replacePreview(null);
    setVersions([]);
    setDeliveries([]);
    setLoading((old) => ({ ...old, preview: false }));
    if (!selected) return;
    const owns = () =>
      mounted.current &&
      id === selectionId.current &&
      selectionRef.current.versionId === selected.public_id;
    Promise.all([
      apiClient.get(withApiPath(`/documents/${selected.public_id}/versions/`)),
      apiClient
        .get(withApiPath(`/documents/${selected.public_id}/email/`))
        .catch(() => ({ data: [] })),
    ])
      .then(([v, d]) => {
        if (owns()) {
          setVersions(v.data);
          setDeliveries(d.data);
        }
      })
      .catch(() => {
        if (owns()) setError("Document history could not be loaded.");
      });
    if (selected.status === "AVAILABLE") {
      setLoading((old) => ({ ...old, preview: true }));
      apiClient
        .get(withApiPath(`/documents/${selected.public_id}/preview/`), {
          responseType: "blob",
        })
        .then((r) => {
          const url = URL.createObjectURL(r.data);
          if (owns()) replacePreview(url);
          else URL.revokeObjectURL(url);
        })
        .catch(() => {
          if (owns())
            setPreview({
              url: null,
              error: "The selected PDF is unavailable.",
            });
        })
        .finally(() => {
          if (owns()) setLoading((old) => ({ ...old, preview: false }));
        });
    }
    return () => {
      selectionId.current += 1;
    };
  }, [selected, replacePreview, previewAttempt]);
  const series = useMemo(
    () =>
      TYPES.filter(
        ([type]) =>
          type !== "CARRIER_RATE_CONFIRMATION" ||
          settings?.capabilities?.can_view_carrier_financials,
      ).map(([type, label]) => ({
        type,
        label,
        document:
          documents
            .filter(
              (d) =>
                d.document_type === type &&
                d.status !== "ARCHIVED" &&
                d.is_current,
            )
            .sort((a, b) => b.version - a.version)[0] ||
          documents
            .filter((d) => d.document_type === type && d.status !== "ARCHIVED")
            .sort((a, b) => b.version - a.version)[0],
      })),
    [documents, settings],
  );
  const selectType = (type, document) =>
    setSelection({
      type,
      seriesId: document?.series_id || null,
      versionId: document?.public_id || null,
    });
  const selectVersion = (document) =>
    setSelection({
      type: document.document_type,
      seriesId: document.series_id,
      versionId: document.public_id,
    });
  const act = async (fn, kind, preferred) => {
    setOperations((old) => ({ ...old, [kind]: old[kind] + 1 }));
    setError("");
    try {
      const response = await fn();
      await refresh(
        preferred || {
          ...selectionRef.current,
          type: response?.data?.document_type || selectionRef.current.type,
          versionId:
            response?.data?.public_id || selectionRef.current.versionId,
        },
      );
      if (mounted.current) setModal(null);
    } catch (e) {
      if (mounted.current) setError(documentOperationError(e));
    } finally {
      if (mounted.current)
        setOperations((old) => ({
          ...old,
          [kind]: Math.max(0, old[kind] - 1),
        }));
    }
  };
  const generate = (options) =>
    act(
      () =>
        selected?.source_type === "GENERATED"
          ? apiClient.post(
              withApiPath(`/documents/${selected.public_id}/regenerate/`),
              {
                base_load_revision: settings.load_revision,
                options,
                idempotency_key: uuid(),
              },
            )
          : apiClient.post(
              withApiPath(`/loads/${loadId}/documents/generate/`),
              {
                document_type: modal.type,
                base_load_revision: settings.load_revision,
                options,
                idempotency_key: uuid(),
              },
            ),
      "generation",
    );
  const upload = (type, file) => {
    const body = new FormData();
    body.append("document_type", type);
    body.append("file", file);
    body.append("idempotency_key", uuid());
    return act(
      () =>
        apiClient.post(withApiPath(`/loads/${loadId}/documents/upload/`), body),
      "upload",
    );
  };
  const email = (form) =>
    act(
      () =>
        apiClient.post(withApiPath(`/documents/${selected.public_id}/email/`), {
          to_recipients: splitEmails(form.to),
          cc_recipients: splitEmails(form.cc),
          bcc_recipients: splitEmails(form.bcc),
          reply_to: form.reply,
          subject: form.subject,
          message_text: form.message,
          attachment_public_ids: form.attachmentIds,
          idempotency_key: uuid(),
        }),
      "email",
    );
  const download = async () => {
    try {
      const r = await apiClient.get(
        withApiPath(`/documents/${selected.public_id}/download/`),
        { responseType: "blob" },
      );
      const u = URL.createObjectURL(r.data),
        a = document.createElement("a");
      a.href = u;
      a.download = `${selected.name}-v${selected.version}.pdf`;
      a.click();
      URL.revokeObjectURL(u);
    } catch (e) {
      setError(documentOperationError(e));
    }
  };
  const retryPreview = () => setPreviewAttempt((value) => value + 1);
  const closeSettings = useCallback(async () => {
    setModal(null);
    setLoading((old) => ({ ...old, settings: true }));
    await refresh(selectionRef.current);
    if (mounted.current) setLoading((old) => ({ ...old, settings: false }));
  }, [refresh]);
  const activeOperation = Object.keys(operations).find(
    (key) => operations[key],
  );
  return (
    <section
      className={styles.shell}
      aria-label="Shipment documents"
      data-testid="documents-console"
    >
      <header>
        <h2>SHIPMENT DOCUMENTS</h2>
        <div>
          {settings?.capabilities?.can_manage_document_settings && (
            <button onClick={() => setModal({ kind: "settings" })}>
              Settings
            </button>
          )}
          <button onClick={onClose}>Close</button>
        </div>
      </header>
      <nav className={styles.toolbar}>
        {selected?.source_type === "GENERATED" && selected.is_current && (
          <button
            onClick={() =>
              setModal({ kind: "generate", type: selected.document_type })
            }
          >
            Update
          </button>
        )}
        {!selected &&
          selection.type &&
          !["POD", "OTHER"].includes(selection.type) && (
            <button
              onClick={() =>
                setModal({ kind: "generate", type: selection.type })
              }
            >
              Generate{" "}
              {selection.type === "BILL_OF_LADING"
                ? "BOL"
                : "Rate Confirmation"}
            </button>
          )}
        <button
          disabled={!settings?.capabilities?.can_upload_documents}
          onClick={() => setModal({ kind: "upload" })}
        >
          Upload
        </button>
        <button
          disabled={!selected || selected.status !== "AVAILABLE"}
          onClick={download}
        >
          Download
        </button>
        <button
          disabled={!preview.url}
          onClick={() => frame.current?.contentWindow?.print()}
        >
          Print
        </button>
        <button
          disabled={
            !selected ||
            selected.status !== "AVAILABLE" ||
            !settings?.capabilities?.can_email_documents
          }
          onClick={() => setModal({ kind: "email" })}
        >
          Email
        </button>
        {selected?.source_type === "UPLOADED" &&
          selected.status !== "ARCHIVED" && (
            <button
              onClick={() =>
                act(
                  () =>
                    apiClient.post(
                      withApiPath(`/documents/${selected.public_id}/archive/`),
                    ),
                  "archive",
                  { type: selection.type, seriesId: null, versionId: null },
                )
              }
            >
              Archive
            </button>
          )}
      </nav>
      {(loading.documents || loading.settings || activeOperation) && (
        <p className={styles.busy}>
          {loading.documents
            ? "Loading documents…"
            : loading.settings
              ? "Refreshing settings…"
              : `${activeOperation}…`}
        </p>
      )}
      {error && (
        <p className={styles.error} role="alert">
          {error}{" "}
          {error === "Documents could not be loaded." && (
            <button onClick={() => refresh()}>Retry</button>
          )}
        </p>
      )}
      <div className={styles.panes}>
        <DocumentList
          series={series}
          selectedDocumentType={selection.type}
          onSelect={selectType}
        />
        <DocumentPreview
          ref={frame}
          url={preview.url}
          document={selected}
          documentType={selection.type}
          loading={loading.preview}
          error={preview.error}
          onRetry={retryPreview}
          canAct={
            ["POD", "OTHER"].includes(selection.type)
              ? Boolean(settings?.capabilities?.can_upload_documents)
              : Boolean(
                  settings?.supported_document_types?.includes(selection.type),
                )
          }
          onEmptyAction={() =>
            setModal(
              ["POD", "OTHER"].includes(selection.type)
                ? { kind: "upload" }
                : { kind: "generate", type: selection.type },
            )
          }
        />
        <DocumentDetails
          selected={selected}
          documentType={selection.type}
          versions={versions}
          deliveries={deliveries}
          onSelect={selectVersion}
          onRetry={(id) =>
            act(
              () =>
                apiClient.post(
                  withApiPath(`/document-deliveries/${id}/retry/`),
                ),
              "email",
            )
          }
        />
      </div>
      {modal?.kind === "generate" && (
        <GenerateDocumentModal
          type={modal.type}
          settings={settings}
          updating={
            selected?.is_current && selected.document_type === modal.type
          }
          operationError={error}
          onClose={() => setModal(null)}
          onSubmit={generate}
        />
      )}{" "}
      {modal?.kind === "upload" && (
        <UploadDocumentModal
          initialType={selection.type}
          operationError={error}
          onClose={() => setModal(null)}
          onSubmit={upload}
        />
      )}{" "}
      {modal?.kind === "email" && selected && (
        <EmailDocumentModal
          selected={selected}
          documents={documents}
          operationError={error}
          onClose={() => setModal(null)}
          onSubmit={email}
        />
      )}{" "}
      {modal?.kind === "settings" && (
        <DocumentSettings loadId={loadId} onClose={closeSettings} />
      )}
    </section>
  );
}
